// Angular Imports
import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';

// 3rd party imports
import {
    DxDataGridModule,
    DxBoxModule,
    DxLoadPanelModule,
    DxDropDownBoxModule,
    DxDropDownButtonModule,
    DxPopupModule,
    DxFormModule,
    DxListModule,
    DxButtonModule,
    DxDateBoxModule,
    DxNumberBoxModule,
    DxTextBoxModule,
    DxTextAreaModule,
    DxTabPanelModule,
    DxTemplateModule,
    DxSelectBoxModule,
    DxFileUploaderModule,
    DxMenuModule,
    DxTreeViewModule,
    DxCheckBoxModule,
    DxValidatorModule,
    DxValidationGroupModule,
    DxSwitchModule,
    DxTabsModule,
    DxToolbarModule,
    DxDrawerModule,
    DxPopoverModule,
    DxTagBoxModule,
    DxRadioGroupModule,
    DxScrollViewModule,
    DxColorBoxModule
} from 'devextreme-angular';
import { AngularSplitModule } from 'angular-split';
import { DxiTotalItemModule } from 'devextreme-angular/ui/nested';

// CC-Common imports
import { CCCommonModule, CCDataGridFeaturesModule } from 'cc-common';

// Module imports
import { ManagementCommonModule } from '../../management-common/management-common.module';
import { SharedModule } from '../../shared-module/shared.module';
import { SharedCirabooksModule } from '../shared-cirabooks-module/shared-cirabooks.module';
import { SharedWorkflowModule } from '../../shared-workflow-module/shared-workflow.module';

// Directive imports
import { AddNewPaymentRowDirective } from './_directive/add-new-payment-row.directive';

// Service Imports
import { CBHelperService } from '../shared-cirabooks-module/_services/shared-services/cb-helper-service';

// Components imports
import { CashReceiptsComponent } from './cash-management/cash-receipts/cash-receipts.component';
import { DepositsComponent } from './cash-management/deposits/deposits.component';
import { DepositEditorComponent } from './cash-management/deposit-editor/deposit-editor.component';
import { ARLedgerComponent } from './account-receivable/ar-ledger/ar-ledger.component';
import { CustomerAgingComponent } from './account-receivable/customer-aging/customer-aging.component';
import { APLedgerComponent } from './accounts-payable/ap-ledger/ap-ledger.component';
import { VendorAgingComponent } from './accounts-payable/vendor-aging/vendor-aging.component';
import { BankAccountsComponent } from './cash-management/bank-accounts/bank-accounts.component';
import { BankAccountEditorComponent } from './cash-management/bank-accounts/bank-account-editor/bank-account-editor.component';
import { BankingPartnerEditorComponent } from './cash-management/bank-accounts/banking-partner-editor/banking-partner-editor.component';
import { BankSignatureEditorComponent } from './cash-management/bank-accounts/bank-signature-editor/bank-signature-editor.component';
import { BankSignatureCreatorComponent } from './cash-management/bank-accounts/bank-signature-creator/bank-signature-creator.component';
import { CBBatchesComponent } from './cb-batches/cb-batches.component';
import { HistoryViewerComponent } from './cash-management/bank-accounts/history-viewer/history-viewer.component';
import { ReconciliationReopenEditorComponent } from './cash-management/bank-reconciliation/reconciliation-reopen-editor/reconciliation-reopen-editor.component';
import { ReconciliationReportViewerComponent } from './cash-management/bank-reconciliation/reconciliation-report-viewer/reconciliation-report-viewer.component';
import { BankReconciliationComponent } from './cash-management/bank-reconciliation/bank-reconciliation.component';
import { EFTVendorAccountsComponent } from './cash-management/bank-accounts/eft-vendor-accounts/eft-vendor-accounts.component';
import { OpenItemsViewerComponent } from './cash-management/bank-accounts/open-items-viewer/open-items-viewer.component';
import { OpenItemEditorComponent } from './cash-management/bank-accounts/open-item-editor/open-item-editor.component';
import { MultiSelectCustomerComponent } from './common/multiselect-customer/multiselect-customer.component';
import { ARInvoiceAnalysisComponent } from './account-receivable/ar-invoice-analysis/ar-invoice-analysis.component';
import { ConfigurationFormItemComponent } from './common/configuration-formitem/configuration-formitem.component';
import { OverridableFormItemComponent } from './common/overridable-formitem/overridable-formitem.component';
import { CBConfigurationSelectorComponent } from './cb-configuration/cb-configuration-selector/cb-configuration-selector.component';
import { CBEntityConfigurationComponent } from './cb-configuration/cb-entity-configuration/cb-entity-configuration.component';
import { CBECGeneralInfoEditorComponent } from './cb-configuration/cb-entity-configuration/cb-ec-general-info-editor/cb-ec-general-info-editor.component';
import { CBECAccountsConfigEditorComponent } from './cb-configuration/cb-entity-configuration/cb-ec-accounts-config-editor/cb-ec-accounts-config-editor.component';
import { CBGLBaseAccountsConfigComponent } from './cb-configuration/cb-gl-base-accounts-config/cb-gl-base-accounts-config.component';
import { CBGLBaseAccountsConfigEditorComponent } from './cb-configuration/cb-gl-base-accounts-config/cb-gl-base-accounts-config-editor/cb-gl-base-accounts-config-editor.component';
import { CBGLBaseAccountsDimensionEditorComponent } from './cb-configuration/cb-gl-base-accounts-config/cb-gl-base-accounts-dimension-editor/cb-gl-base-accounts-dimension-editor.component';
import { CBGLSubAccountsConfigComponent } from './cb-configuration/cb-gl-sub-accounts-config/cb-gl-sub-accounts-config.component';
import { CBGLSubAccountsConfigEditorComponent } from './cb-configuration/cb-gl-sub-accounts-config/cb-gl-sub-accounts-config-editor/cb-gl-sub-accounts-config-editor.component';
import { CBGLSubCategoriesConfigComponent } from './cb-configuration/cb-gl-sub-categories-config/cb-gl-sub-categories-config.component';
import { CBGLSubCategoriesConfigEditorComponent } from './cb-configuration/cb-gl-sub-categories-config/cb-gl-sub-categories-config-editor/cb-gl-sub-categories-config-editor.component';
import { CBGLAccountGroupsConfigComponent } from './cb-configuration/cb-gl-account-groups-config/cb-gl-account-groups-config.component';
import { CBGLAccountGroupsConfigEditorComponent } from './cb-configuration/cb-gl-account-groups-config/cb-gl-account-groups-config-editor/cb-gl-account-groups-config-editor.component';
import { CBGLAccountGroupsAssignSubAccountsComponent } from './cb-configuration/cb-gl-account-groups-config/cb-gl-account-groups-assign-sub-accounts/cb-gl-account-groups-assign-sub-accounts.component';
import { CBGLAccountGroupAssignedReportsComponent } from './cb-configuration/cb-gl-account-groups-config/cb-gl-account-group-assigned-reports/cb-gl-account-group-assigned-reports.component';
import { CBGLAccountGroupAssignedReportsEditorComponent } from './cb-configuration/cb-gl-account-groups-config/cb-gl-account-group-assigned-reports-editor/cb-gl-account-group-assigned-reports-editor.component';
import { CBGLAccountGroupDisplayOrderEditorComponent } from './cb-configuration/cb-gl-account-groups-config/cb-gl-account-group-display-order-editor/cb-gl-account-group-display-order-editor.component';
import { CBGLAccountGroupConfigHistoryComponent } from './cb-configuration/cb-gl-account-groups-config/cb-gl-account-group-config-history/cb-gl-account-group-config-history.component';
import { CBGLAccountLabelsConfigComponent } from './cb-configuration/cb-gl-account-labels-config/cb-gl-account-labels-config.component';
import { CBGLAccountLabelsConfigEditorComponent } from './cb-configuration/cb-gl-account-labels-config/cb-gl-account-labels-config-editor/cb-gl-account-labels-config-editor.component';
import { CBGLDimensionsConfigComponent } from './cb-configuration/cb-gl-dimensions/cb-gl-dimensions-config/cb-gl-dimensions-config.component';
import { CBGLDimensionsConfigEditorComponent } from './cb-configuration/cb-gl-dimensions/cb-gl-dimensions-config/cb-gl-dimensions-config-editor/cb-gl-dimensions-config-editor.component';
import { CBGLDimensionOptionsConfigComponent } from './cb-configuration/cb-gl-dimensions/cb-gl-dimension-options-config/cb-gl-dimension-options-config.component';
import { CBGLDimensionOptionsConfigEditorComponent } from './cb-configuration/cb-gl-dimensions/cb-gl-dimension-options-config/cb-gl-dimension-options-config-editor/cb-gl-dimension-options-config-editor.component';
import { CBGLDimensionsComponent } from './cb-configuration/cb-gl-dimensions/cb-gl-dimensions.component';
import { CBGLDimensionGroupConfigComponent } from './cb-configuration/cb-gl-dimension-group-config/cb-gl-dimension-group-config.component';
import { CBGLDimensionGroupConfigEditorComponent } from './cb-configuration/cb-gl-dimension-group-config/cb-gl-dimension-group-config-editor/cb-gl-dimension-group-config-editor.component';
import { CBConfigurationComponent } from './cb-configuration/cb-configuration.component';
import { CustomerAgingSummaryDetailComponent } from './account-receivable/customer-aging/customer-aging-summary-detail/customer-aging-summary-detail.component';
import { VendorAgingSummaryDetailComponent } from './accounts-payable/vendor-aging/vendor-aging-summary-detail/vendor-aging-summary-detail.component';
import { TrialBalanceComponent } from './general-ledger/trial-balance/trial-balance.component';
import { GLReportComponent } from './general-ledger/gl-report/gl-report.component';
import { OpenCloseBooksComponent } from './general-ledger/open-close-books/open-close-books.component';
import { APBillAnalysisComponent } from './accounts-payable/ap-bill-analysis/ap-bill-analysis.component';
import { JournalEntryTemplateComponent } from './general-ledger/journal-entry-template/journal-entry-template.component';
import { JournalEntryTemplateEditorComponent } from './general-ledger/journal-entry-template/journal-entry-template-editor/journal-entry-template-editor.component';
import { JETemplateGeneralSetupComponent } from './general-ledger/journal-entry-template/journal-entry-template-editor/jet-general-setup/jet-general-setup.component';
import { JETemplateScheduleSetupComponent } from './general-ledger/journal-entry-template/journal-entry-template-editor/jet-schedule-setup/jet-schedule-setup.component';
import { EFTPaymentManagementComponent } from './accounts-payable/eft-payment-management/eft-payment-management.component';
import { PrepaidPaymentComponent } from './accounts-payable/eft-payment-management/prepaid-payment/prepaid-payment.component';
import { CheckPrintingManagementComponent } from './accounts-payable/check-printing-management/check-printing-management.component';
import { CheckPrintingBillsToPayComponent } from './accounts-payable/check-printing-management/check-printing-bills-to-pay/check-printing-bills-to-pay.component';
import { CheckPrintingPaidChecksComponent } from './accounts-payable/check-printing-management/check-printing-paid-checks/check-printing-paid-checks.component';
import { GLBudgetComponent } from './general-ledger/gl-budget/gl-budget.component';
import { GLBudgetCreatorComponent } from './general-ledger/gl-budget/gl-budget-creator/gl-budget-creator.component';
import { GLBudgetDimensionEditorComponent } from './general-ledger/gl-budget/gl-budget-dimension-editor/gl-budget-dimension-editor.component';
import { VendorTenNinetyNineComponent } from './accounts-payable/vendor-ten-ninety-nine/vendor-ten-ninety-nine.component';
import { CreateCashReceiptsComponent } from './cash-management/create-cash-receipts/create-cash-receipts.component';
import { ARMassUploadComponent } from './account-receivable/ar-mass-upload/ar-mass-upload.component';
import { JournalEntriesComponent } from './general-ledger/journal-entry/journal-entries.component';
import { JournalEntryEditorComponent } from './general-ledger/journal-entry/journal-entry-editor/journal-entry-editor.component';
import { UploadJournalEntryComponent } from './general-ledger/upload-journal-entry/upload-journal-entry.component';
import { JournalEntryTemplateSelectorComponent } from './general-ledger/journal-entry/journal-entry-template-selector/journal-entry-template-selector.component';
import { JournalEntryReverseEditorComponent } from './general-ledger/journal-entry/journal-entry-reverse-editor/journal-entry-reverse-editor.component';
import { FundsTransferComponent } from './cash-management/funds-transfer/funds-transfer.component';
import { ScheduledTransferComponent } from './cash-management/funds-transfer/scheduled-transfer/scheduled-transfer.component';
import { RecurringTransferComponent } from './cash-management/funds-transfer/recurring-transfer/recurring-transfer.component';
import { FundsTransferHistoryComponent } from './cash-management/funds-transfer/funds-transfer-history/funds-transfer-history.component';
import { CompletedTransferComponent } from './cash-management/funds-transfer/completed-transfer/completed-transfer.component';
import { VoidPaymentEditorComponent } from './accounts-payable/check-printing-management/check-printing-paid-checks/void-payment-editor/void-payment-editor.component';
import { ManualCheckPaymentComponent } from './accounts-payable/check-printing-management/check-printing-bills-to-pay/manual-check-payment/manual-check-payment.component';
import { UploadBudgetsComponent } from './general-ledger/upload-budgets/upload-budgets.component';
import { UnmatchedTransactionsComponent } from './cash-management/unmatched-transactions/unmatched-transactions.component';
import { ViewTransactionsComponent } from './cash-management/unmatched-transactions/view-transactions/view-transactions.component';
import { AddBankInterestComponent } from './cash-management/unmatched-transactions/add-bank-interest/add-bank-interest.component';
import { PostEftPaymentComponent } from './cash-management/unmatched-transactions/post-eft-payment/post-eft-payment.component';
import { FundsTransferEditorComponent } from './cash-management/funds-transfer/funds-transfer-editor/funds-transfer-editor.component';
import { FundsTransferViewerComponent } from './cash-management/funds-transfer/funds-transfer-viewer/funds-transfer-viewer.component';
import { CreateARPaymentsComponent } from './account-receivable/create-ar-payments/create-ar-payments.component';
import { LotPaymentAllocationComponent } from './account-receivable/lot-payment-allocation/lot-payment-allocation.component';
import { LotPaymentAllocationFiltersComponent } from './account-receivable/lot-payment-allocation/lot-payment-allocation-filters/lot-payment-allocation-filters.component';
import { LotPaymentAllocationGridComponent } from './account-receivable/lot-payment-allocation/lot-payment-allocation-grid/lot-payment-allocation-grid.component';
import { BuilderStatementsComponent } from './account-receivable/builder-statements/builder-statements.component';
import { BuilderStatementsFiltersComponent } from './account-receivable/builder-statements/builder-statements-filters/builder-statements-filters.component';
import { BuilderStatementsGridComponent } from './account-receivable/builder-statements/builder-statements-grid/builder-statements-grid.component';
import { OverridableHtmlEditorComponent } from './common/overridable-html-editor/overridable-html-editor.component';
import { CBAuditLogComponent } from './audit-log/cb-audit-log/cb-audit-log.component';
import { AuditLogComponent } from './audit-log/cb-audit-log/audit-log/audit-log.component';
import { SetupLocalOfficeOnlyCheckPrintingComponent } from './accounts-payable/setup-local-office-only-check-printing/setup-local-office-only-check-printing.component';
import { AddEditLocalOfficeCheckPrintingComponent } from './accounts-payable/setup-local-office-only-check-printing/add-edit-local-office-check-printing/add-edit-local-office-check-printing.component';
import { BankAccountNotesComponent } from './cash-management/bank-accounts/bank-account-notes/bank-account-notes.component';
import { BankAccountNoteViewEditComponent } from './cash-management/bank-accounts/bank-account-notes/bank-account-note-view-edit/bank-account-note-view-edit.component';

@NgModule({
    declarations: [
        CashReceiptsComponent,
        DepositsComponent,
        DepositEditorComponent,
        ARLedgerComponent,
        CustomerAgingComponent,
        APLedgerComponent,
        VendorAgingComponent,
        CBBatchesComponent,
        BankAccountsComponent,
        BankAccountEditorComponent,
        BankAccountNotesComponent,
        BankAccountNoteViewEditComponent,
        BankingPartnerEditorComponent,
        BankSignatureEditorComponent,
        BankSignatureCreatorComponent,
        HistoryViewerComponent,
        MultiSelectCustomerComponent,
        CustomerAgingSummaryDetailComponent,
        EFTVendorAccountsComponent,
        OpenItemsViewerComponent,
        OpenItemEditorComponent,
        ARInvoiceAnalysisComponent,
        ReconciliationReopenEditorComponent,
        ReconciliationReportViewerComponent,
        BankReconciliationComponent,
        ConfigurationFormItemComponent,
        OverridableFormItemComponent,
        CBConfigurationSelectorComponent,
        CBEntityConfigurationComponent,
        CBECGeneralInfoEditorComponent,
        CBECAccountsConfigEditorComponent,
        CBGLBaseAccountsConfigComponent,
        CBGLBaseAccountsConfigEditorComponent,
        CBGLBaseAccountsDimensionEditorComponent,
        CBGLSubAccountsConfigComponent,
        CBGLSubAccountsConfigEditorComponent,
        CBGLSubCategoriesConfigComponent,
        CBGLSubCategoriesConfigEditorComponent,
        CBGLAccountGroupsConfigComponent,
        CBGLAccountGroupsConfigEditorComponent,
        CBGLAccountGroupsAssignSubAccountsComponent,
        CBGLAccountGroupAssignedReportsComponent,
        CBGLAccountGroupAssignedReportsEditorComponent,
        CBGLAccountGroupDisplayOrderEditorComponent,
        CBGLAccountGroupConfigHistoryComponent,
        CBGLAccountLabelsConfigComponent,
        CBGLAccountLabelsConfigEditorComponent,
        CBGLDimensionsConfigComponent,
        CBGLDimensionsConfigEditorComponent,
        CBGLDimensionOptionsConfigComponent,
        CBGLDimensionOptionsConfigEditorComponent,
        CBGLDimensionsComponent,
        CBGLDimensionGroupConfigComponent,
        CBGLDimensionGroupConfigEditorComponent,
        CBConfigurationComponent,
        VendorAgingSummaryDetailComponent,
        TrialBalanceComponent,
        GLReportComponent,
        OpenCloseBooksComponent,
        APBillAnalysisComponent,
        JournalEntryTemplateComponent,
        JournalEntryTemplateEditorComponent,
        JETemplateGeneralSetupComponent,
        JETemplateScheduleSetupComponent,
        EFTPaymentManagementComponent,
        PrepaidPaymentComponent,
        CheckPrintingManagementComponent,
        CheckPrintingBillsToPayComponent,
        CheckPrintingPaidChecksComponent,
        GLBudgetComponent,
        GLBudgetCreatorComponent,
        GLBudgetDimensionEditorComponent,
        VendorTenNinetyNineComponent,
        CreateCashReceiptsComponent,
        ARMassUploadComponent,
        JournalEntriesComponent,
        JournalEntryEditorComponent,
        UploadJournalEntryComponent,
        JournalEntryTemplateSelectorComponent,
        JournalEntryReverseEditorComponent,
        FundsTransferComponent,
        ScheduledTransferComponent,
        RecurringTransferComponent,
        FundsTransferHistoryComponent,
        CompletedTransferComponent,
        VoidPaymentEditorComponent,
        ManualCheckPaymentComponent,
        UploadBudgetsComponent,
        UnmatchedTransactionsComponent,
        ViewTransactionsComponent,
        AddBankInterestComponent,
        PostEftPaymentComponent,
        CompletedTransferComponent,
        FundsTransferEditorComponent,
        FundsTransferViewerComponent,
        CreateARPaymentsComponent,
        LotPaymentAllocationComponent,
        LotPaymentAllocationFiltersComponent,
        LotPaymentAllocationGridComponent,
        BuilderStatementsComponent,
        BuilderStatementsFiltersComponent,
        BuilderStatementsGridComponent,
        OverridableHtmlEditorComponent,
        CBAuditLogComponent,
        AuditLogComponent,
        SetupLocalOfficeOnlyCheckPrintingComponent,
        AddEditLocalOfficeCheckPrintingComponent,
        AddNewPaymentRowDirective
    ],
    imports: [
        CommonModule,
        DxBoxModule,
        DxDataGridModule,
        DxLoadPanelModule,
        DxDropDownBoxModule,
        DxDropDownButtonModule,
        DxPopupModule,
        DxFormModule,
        DxListModule,
        DxButtonModule,
        DxDateBoxModule,
        DxNumberBoxModule,
        DxTextBoxModule,
        DxTextAreaModule,
        DxTabPanelModule,
        DxTemplateModule,
        DxMenuModule,
        DxTreeViewModule,
        DxSelectBoxModule,
        DxFileUploaderModule,
        DxValidatorModule,
        DxValidationGroupModule,
        DxCheckBoxModule,
        DxSwitchModule,
        DxTabsModule,
        DxToolbarModule,
        DxDrawerModule,
        DxPopoverModule,
        DxTagBoxModule,
        DxRadioGroupModule,
        DxScrollViewModule,
        DxColorBoxModule,
        AngularSplitModule,
        CCCommonModule,
        CCDataGridFeaturesModule,
        SharedModule,
        ManagementCommonModule,
        SharedCirabooksModule,
        SharedWorkflowModule,
        DxiTotalItemModule
    ],
    exports: [
        CashReceiptsComponent,
        DepositsComponent,
        DepositEditorComponent,
        ARLedgerComponent,
        CustomerAgingComponent,
        APLedgerComponent,
        VendorAgingComponent,
        CBBatchesComponent,
        BankAccountsComponent,
        BankAccountEditorComponent,
        ARInvoiceAnalysisComponent,
        BankReconciliationComponent,
        CBConfigurationComponent,
        TrialBalanceComponent,
        GLReportComponent,
        OpenCloseBooksComponent,
        APBillAnalysisComponent,
        JournalEntryTemplateComponent,
        CheckPrintingManagementComponent,
        GLBudgetComponent,
        VendorTenNinetyNineComponent,
        EFTPaymentManagementComponent,
        PrepaidPaymentComponent,
        CheckPrintingManagementComponent,
        CreateCashReceiptsComponent,
        ARMassUploadComponent,
        JournalEntriesComponent,
        UploadJournalEntryComponent,
        ConfigurationFormItemComponent,
        OverridableFormItemComponent,
        FundsTransferComponent,
        UploadBudgetsComponent,
        UnmatchedTransactionsComponent,
        CreateARPaymentsComponent,
        LotPaymentAllocationComponent,
        BuilderStatementsComponent,
        OverridableHtmlEditorComponent,
        JournalEntryEditorComponent,
        JournalEntryTemplateSelectorComponent,
        MultiSelectCustomerComponent,
        AuditLogComponent
    ],
    providers: [CBHelperService]
})
export class CirabooksModule {}
